import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useMessages } from '../../contexts/MessageContext';
import NotificationsPopover from '../notifications/NotificationsPopover';
import financialRequestService from '../../services/financialRequestService';
import {
  Box, Drawer, Toolbar, List, ListItem, ListItemIcon, 
  ListItemText, Avatar, Typography, Divider, Chip,
  useTheme, useMediaQuery, IconButton, AppBar,
  Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Button, ListItemButton, Badge, Paper,
  CircularProgress, alpha, Alert, Slide, Zoom, styled,
  Backdrop
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  TrendingUp as TrendingUpIcon,
  ReceiptLong as ReceiptIcon,
  MonetizationOn as MonetizationOnIcon,
  Schedule as ScheduleIcon,
  SelfImprovement as MentalWellnessIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  AccountCircle as AccountCircleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccountTree as AccountTreeIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

// Transition component for logout dialog
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled component for the security icon
const StyledSecurityIcon = styled(SecurityIcon)(({ theme }) => ({
  fontSize: 45,
  color: alpha(theme.palette.primary.main, 0.9),
  marginRight: theme.spacing(2),
}));

const AdminSidebar = ({ children }) => {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const theme = useTheme();
  const { primaryColor } = useThemeContext();
  const { unreadCount } = useMessages();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [newRequestCount, setNewRequestCount] = useState(0);
  const [completedRequestCount, setCompletedRequestCount] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutSuccess, setLogoutSuccess] = useState(false);
  const [loginTime, setLoginTime] = useState(null);

  // Safe function to get initials
  const getInitials = (firstName, lastName) => {
    const firstInitial = (firstName && typeof firstName === 'string' && firstName.length > 0) ? firstName.charAt(0) : '';
    const lastInitial = (lastName && typeof lastName === 'string' && lastName.length > 0) ? lastName.charAt(0) : '';
    return (firstInitial + lastInitial) || '?';
  };

  // Fetch financial request counts for alerts
  useEffect(() => {
    const fetchFinancialRequestCounts = async () => {
      try {
        const response = await financialRequestService.getRequestCounts();
        if (response) {
          setNewRequestCount(response.requested || 0);
          setCompletedRequestCount(response.completed || 0);
        }
      } catch (error) {
        console.error('Error fetching financial request counts:', error);
      }
    };

    if (user) {
      fetchFinancialRequestCounts();
      const intervalId = setInterval(fetchFinancialRequestCounts, 5 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [user]);

  useEffect(() => {
    const storedLoginTime = localStorage.getItem('loginTime');
    if (storedLoginTime) {
      setLoginTime(new Date(parseInt(storedLoginTime)));
    } else {
      const now = new Date();
      setLoginTime(now);
      localStorage.setItem('loginTime', now.getTime().toString());
    }
  }, []);

  const menuItems = [
    { path: "/admin/dashboard", name: "Dashboard", icon: <DashboardIcon /> },
    { path: "/admin/members", name: "Members", icon: <PeopleIcon /> },
    { path: "/admin/membership-analytics", name: "Analytics", icon: <TrendingUpIcon /> },
    { path: "/admin/expenses", name: "Expenses", icon: <ReceiptIcon /> },
 
    { path: "/admin/workflows", name: "Workflows", icon: <TimelineIcon /> },
    { 
      path: "/admin/financial-requests", 
      name: "Financial Requests", 
      icon: newRequestCount > 0 ? (
        <Badge badgeContent={newRequestCount} color="error" sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}>
          <MonetizationOnIcon />
        </Badge>
      ) : <MonetizationOnIcon />
    },
    { 
      path: "/admin/messages", 
      name: "Messages", 
      icon: unreadCount > 0 ? (
        <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}>
          <EmailIcon />
        </Badge>
      ) : <EmailIcon />
    },
    { path: "/admin/report-scheduler", name: "Report Scheduler", icon: <ScheduleIcon /> },
    { path: "/admin/reset", name: "Mental Wellness", icon: <MentalWellnessIcon /> },
    { path: "/admin/subscription", name: "My Subscription", icon: <ReceiptIcon /> },
    { path: "/admin/settings", name: "Settings", icon: <SettingsIcon /> }
  ];

  const openLogoutDialog = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      await logout();
      setLogoutSuccess(true);
      localStorage.removeItem('loginTime');
      setTimeout(() => {
        setLogoutDialogOpen(false);
        setDrawerOpen(false);
        navigate('/login');
      }, 1500);
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.clear();
      navigate('/login');
    }
  };

  const drawerWidth = 240;

  const drawer = (
    <Box>
      <Toolbar sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        py: 2 
      }}>
        <Avatar
          src={user?.profilePicture || ""}
          sx={{ 
            width: 80, 
            height: 80,
            mb: 1,
            bgcolor: primaryColor || theme.palette.primary.main
          }}
          alt={`${user?.firstName || 'Admin'} ${user?.lastName || 'User'}`}
        >
          {getInitials(user?.firstName, user?.lastName)}
        </Avatar>
        <Typography variant="h6" sx={{ mt: 1 }}>
          {user?.firstName || 'Admin'} {user?.lastName || 'User'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {user?.email || 'admin@example.com'}
        </Typography>
        <Chip
          label="Administrator"
          sx={{ 
            mt: 1, 
            bgcolor: primaryColor || theme.palette.primary.main,
            color: 'white'
          }}
          size="small"
        />
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || 
                          (item.path !== "/admin/dashboard" && location.pathname.startsWith(item.path));
          
          return (
            <ListItem 
              disablePadding
              key={item.path}
            >
              <ListItemButton
                component={Link} 
                to={item.path}
                selected={isActive}
                onClick={() => isMobile && setDrawerOpen(false)}
                sx={{
                  borderLeft: isActive ? `4px solid ${primaryColor || theme.palette.primary.main}` : '4px solid transparent',
                  bgcolor: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: isActive ? primaryColor || theme.palette.primary.main : 'rgba(255,255,255,0.7)',
                  }
                }}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.name}
                  primaryTypographyProps={{
                    sx: {
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                      fontWeight: isActive ? '500' : 'normal'
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={openLogoutDialog} 
            sx={{
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{
                sx: { color: 'rgba(255,255,255,0.7)' }
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={4}
        sx={{
          display: { md: 'none' },
          width: '100%',
          bgcolor: primaryColor || theme.palette.primary.main,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {tenant?.name || 'Admin Dashboard'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <NotificationsPopover />
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: '#1a2138',
              color: 'white',
              marginTop: { xs: '56px', sm: '64px' },
            },
          }}
        >
          {drawer}
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: '#1a2138',
              color: 'white'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          px: { xs: 0.5, md: 0 },
          ml: 0,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 1, md: 0 },
        }}
      >
        <Toolbar sx={{ display: { md: 'none' }, minHeight: { xs: '24px' } }} />
        {children}
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => !isLoggingOut && setLogoutDialogOpen(false)}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        TransitionComponent={SlideTransition}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: '90%', sm: '450px' },
            overflow: 'hidden',
            backgroundImage: `radial-gradient(circle at top right, ${alpha(primaryColor || theme.palette.primary.main, 0.08)}, transparent 70%)`,
          }
        }}
      >
        <DialogTitle 
          id="logout-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            pt: 3,
            pb: 2,
            background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.7)}, ${alpha(theme.palette.background.paper, 0.98)})`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          {logoutSuccess ? (
            <Zoom in={logoutSuccess}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 28, mr: 1.5 }} />
                <Typography variant="h6" component="div">
                  Logout Successful
                </Typography>
              </Box>
            </Zoom>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StyledSecurityIcon />
              <Box>
                <Typography variant="h6" component="div">
                  Confirm Logout
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {tenant?.name || 'Admin Portal'} - {new Date().toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {logoutSuccess ? (
            <Alert 
              severity="success" 
              variant="filled"
              sx={{ 
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                borderRadius: 1 
              }}
            >
              You have been successfully logged out. Redirecting to login page...
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  src={user?.profilePicture || ""}
                  sx={{ 
                    width: 50, 
                    height: 50,
                    border: `2px solid ${alpha(primaryColor || theme.palette.primary.main, 0.3)}`,
                    bgcolor: primaryColor || theme.palette.primary.main,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  {getInitials(user?.firstName, user?.lastName)}
                </Avatar>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {user?.firstName || 'Admin'} {user?.lastName || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email || 'admin@example.com'}
                  </Typography>
                </Box>
              </Box>
              
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 2.5, 
                  bgcolor: alpha(theme.palette.primary.light, 0.04),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderRadius: 1.5
                }}
              >
                <Typography variant="body1" paragraph sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <InfoIcon color="primary" sx={{ fontSize: 20, mr: 1, mt: 0.3, opacity: 0.8 }} />
                  Logging out will end your current session. You will need to sign in again to access your account.
                </Typography>
                
                {loginTime && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <AccessTimeIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
                    <Typography variant="body2" color="text.secondary">
                      Session started: {format(loginTime, 'MMM d, yyyy, h:mm a')}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <AccountCircleIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
                  <Typography variant="body2" color="text.secondary">
                    Logged in as Admin | {tenant?.name || 'Organization'}
                  </Typography>
                </Box>
              </Paper>
              
              <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                variant="outlined"
                sx={{ 
                  borderRadius: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                Any unsaved changes may be lost when you log out.
              </Alert>
            </>
          )}
        </DialogContent>
        
        {!logoutSuccess && (
          <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button 
              onClick={() => setLogoutDialogOpen(false)} 
              color="inherit"
              disabled={isLoggingOut}
              sx={{ 
                px: 3, 
                borderRadius: 1.5,
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleLogout} 
              color="error" 
              variant="contained" 
              disabled={isLoggingOut}
              sx={{ 
                px: 3,
                borderRadius: 1.5,
                bgcolor: 'error.main',
                '&:hover': { bgcolor: 'error.dark' },
                boxShadow: 'none'
              }}
              startIcon={isLoggingOut ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}
            >
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={logoutSuccess}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default AdminSidebar;
